import { IResourceComponentsProps, useList, useNavigation, usePermissions, useShow, useDelete } from "@pankod/refine-core";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list';
import { IEvent } from "interfaces";
import { Button, Drawer, Modal, Show, Tooltip, Typography, useModal, Popconfirm } from "@pankod/refine-antd";
import { DeleteOutlined } from "@ant-design/icons";
import { AssignInvoices } from "pages/custom/assign-invoices";
import { useEffect, useRef, useState } from "react";
import { PermissionsConstants } from "constants/permissions";
import { useLocation } from "@pankod/refine-react-router-v6";

const { Title, Text } = Typography;

// Hook personalizado para detectar móviles
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const invoiceIcons: { [key: string]: string } = {
  "1": "🥗",
  "2": "🪑",
  "3": "🎵",
  "4": "🚌",
};

export const EventListPage: React.FC<IResourceComponentsProps> = () => {

  const { create, push } = useNavigation();
  const [isDrawerVisible, setDrawerVisible] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { data: permissionsData } = usePermissions();
  const { mutate: deleteEvent } = useDelete();
  const isMobile = useIsMobile();

  const location: any = useLocation();
  const calendarRef = useRef<FullCalendar | null>(null);

  const [initialView, setInitialView] = useState<string>("dayGridMonth"); // Valor predeterminado


  const { data, isLoading, refetch } = useList<IEvent>({
    resource: "events",
    config: {
      hasPagination: false
    }
  });

  const { modalProps: showModalProps, show, close } = useModal();
  const { queryResult, showId, setShowId } = useShow<any>();
  const { data: showData } = queryResult;
  const showRecord = showData ?.data;

  const events = data?.data.map((event: any) => {
    const start = `${event.event_date}T${event.start_time || "00:00:00"}`;
    const end = event.end_time ? `${event.event_date}T${event.end_time}` : undefined;

    return {
      id: event.id,
      title: event.name,
      start, 
      end,   
      extendedProps: {
        invoiceTypes: event.invoice_types,
      }
    };
  }) || [];

  useEffect(() => {
    if (location.state?.defaultCalendarView) {
      setInitialView(location.state?.defaultCalendarView); // Cambiar la vista si viene de EventCreate
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.defaultCalendarView && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(location.state?.defaultCalendarView);
    }
  }, [location.state]);

  const handleEventClick = (info: any) => {
    setShowId(info.event.id);
    show();
  };

  const toggleDrawer = () => {
    setDrawerVisible((prev) => {
      if (prev) {
        queryResult.refetch();
        setHasChanges(true);
      }
      return !prev;
    });
  };

  const handleCloseModal = () => {
    close();
    if (hasChanges) {
      refetch();
    }
    setDrawerVisible(false);
    setHasChanges(false);
  };

  const handleDeleteEvent = () => {
    if (!showId) return;
    
    deleteEvent({
      resource: "events",
      id: showId,
    }, {
      onSuccess: () => {
        handleCloseModal();
        refetch();
      }
    });
  };

  const renderEventContent = (eventInfo: any) => {
    const { extendedProps } = eventInfo.event;

    const normalizedTimeText = eventInfo.timeText
      ? eventInfo.timeText.includes(":")
        ? eventInfo.timeText
        : `${eventInfo.timeText}:00`
      : "";

    return (
      <div className="calendar-custom-event">
        <div className="calendar-event-invoices">
          <b style={{ color: 'black' }}>{normalizedTimeText}</b>
          {permissionsData?.includes(PermissionsConstants.ACTION_VIEW_ASSIGN_INVOICE) && extendedProps.invoiceTypes?.map((invoiceType: any) => {
            const title = `[${invoiceType.username}] ${invoiceType.description}`;
            return (
              <Tooltip key={`tooltip-${invoiceType.id}-${invoiceType.id_user}`} placement="top" title={title}>
                <span key={invoiceType.id} className="calendar-event-invoices-icons">
                  {invoiceIcons[invoiceType.id] || "❓"}
                </span>
              </Tooltip>
            );
          })}
        </div>
        <i className="calendar-event-title">{eventInfo.event.title}</i>
      </div>
    );
  }

  const hasCreateEventPermission = permissionsData?.includes(PermissionsConstants.ACTION_CREATE_EVENTS);

  const customButtons = hasCreateEventPermission ? {
    newEvent: {
      text: 'Nuevo Evento',
      click: function() {
        create('events');
      }
    }
  } : undefined;
  /*
  const initialCalendarView = defaultCalendarView ? defaultCalendarView : 'dayGridMonth';
  console.log("defaultCalendarView", defaultCalendarView);
  */

  return (
    <div>
      { isLoading ? (
        <p>Cargando...</p>
      ) : (
        <div className="calendar-wrapper">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
            initialView={initialView}
            weekends={true}
            events={events}
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            locale={'es'}
            customButtons={customButtons}
            height="auto"
            contentHeight="auto"
            aspectRatio={isMobile ? 1.0 : 1.35}
            headerToolbar={{
              left: hasCreateEventPermission ? (isMobile ? 'prev,next today newEvent' : 'prev,next today newEvent') : 'prev,next today',
              center: 'title',
              right: isMobile ? 'dayGridMonth listWeek' : 'dayGridMonth,timeGridWeek,timeGridDay listWeek,listMonth'
            }}
            buttonText={{
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día',
              listWeek: 'Lista Semanal',
              listMonth: 'Lista Mensual',
            }}
            themeSystem="bootstrap5"
            eventDisplay="block"
            displayEventTime={true}
          />
        </div>
      )}

      <Modal 
        {...showModalProps} 
        onCancel={handleCloseModal} 
        width={isMobile ? "100vw" : 900}
        className={`event-detail-modal ${isMobile ? 'event-detail-modal-mobile' : ''}`}
        bodyStyle={{ padding: isMobile ? "16px" : "24px" }}
        style={isMobile ? { 
          top: 0, 
          paddingBottom: 0, 
          margin: 0,
          maxWidth: "100vw", 
          height: "100vh" 
        } : {}}
        maskClosable={!isMobile}
        destroyOnClose={true}
        centered={!isMobile}
        footer={null}
      >
        <Show
          isLoading={isLoading}
          recordItemId={showId}
          headerProps={{
            title: "Detalle del Evento",
          }}
          headerButtons={() => (
            <div className="event-modal-actions">
              { hasCreateEventPermission && (
                <>
                  <Popconfirm
                    title="¿Estás seguro de eliminar este evento? Esta acción no se puede deshacer."
                    onConfirm={handleDeleteEvent}
                    okText="Sí, eliminar"
                    cancelText="Cancelar"
                  >
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      size={isMobile ? "small" : "middle"}
                    >
                      {!isMobile && "Eliminar"}
                    </Button>
                  </Popconfirm>
                  <Button
                    type="primary"
                    onClick={() => push(`/events/edit/${showRecord?.id}`)}
                    size={isMobile ? "small" : "middle"}
                  >
                    Editar
                  </Button>
                </>
              )}
              { permissionsData?.includes(PermissionsConstants.ACTION_ASSIGN_INVOICE) && (
                <Button 
                  onClick={toggleDrawer}
                  size={isMobile ? "small" : "middle"}
                >
                  {isDrawerVisible ? "Cerrar Facturas" : "Asignar Facturas"} &rarr;
                </Button>
              )}
            </div>
          )}
        >
          <div className="event-detail-content">
            <div className="event-detail-row">
              <div className="event-detail-label">ID:</div>
              <div className="event-detail-value">{showRecord?.id}</div>
            </div>

            <div className="event-detail-row">
              <div className="event-detail-label">Nombre del Evento:</div>
              <div className="event-detail-value event-title">{showRecord?.name}</div>
            </div>

            <div className="event-detail-row">
              <div className="event-detail-label">Tipo de Evento:</div>
              <div className="event-detail-value">{showRecord?.event_type?.name || "--"}</div>
            </div>

            <div className="event-detail-row">
              <div className="event-detail-label">Fecha del evento:</div>
              <div className="event-detail-value event-date">{showRecord?.event_date}</div>
            </div>

            <div className="event-detail-row">
              <div className="event-detail-label">Horario:</div>
              <div className="event-detail-value event-time">
                De <strong>{showRecord?.start_time}</strong> a <strong>{showRecord?.end_time}</strong>
              </div>
            </div>

            <div className="event-detail-row">
              <div className="event-detail-label">¿Asiste Prensa UAS?:</div>
              <div className="event-detail-value">
                <span className={`press-status ${showRecord?.press_visibility?.toLowerCase() === 'si' ? 'yes' : 'no'}`}>
                  {showRecord?.press_visibility}
                </span>
              </div>
            </div>

            { permissionsData?.includes(PermissionsConstants.ACTION_VIEW_ASSIGN_INVOICE) && showRecord?.invoice_types?.length > 0 && (
              <div className="event-detail-row">
                <div className="event-detail-label">Facturas asociadas:</div>
                <div className="event-detail-value">
                  <div className="invoice-icons">
                    {showRecord?.invoice_types?.map((invoiceType: any) => (
                      <Tooltip key={`icon-${invoiceType.id_invoice_type}-${invoiceType.id_user}`} title={`${invoiceType.description}`}>
                        <span className="invoice-icon">
                          {invoiceIcons[invoiceType.id_invoice_type] || "❓"}
                        </span>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="event-detail-row">
              <div className="event-detail-label">¿Quién atiende?:</div>
              <div className="event-detail-value">{showRecord?.attendant?.name || "--"}</div>
            </div>

            <div className="event-detail-row">
              <div className="event-detail-label">¿Quiénes asisten?:</div>
              <div className="event-detail-value">{showRecord?.attendant_exterior || "--"}</div>
            </div>

            <div className="event-detail-row description-row">
              <div className="event-detail-label">Descripción:</div>
              <div className="event-detail-value event-description">
                {showRecord?.description || "--"}
              </div>
            </div>
          </div>
        </Show>

        { isDrawerVisible &&
          <Drawer
            title="Asignar Facturas"
            placement="right"
            closable={true}
            onClose={toggleDrawer}
            visible={isDrawerVisible}
            width={isMobile ? "90%" : 500}
            getContainer={false}
            style={{position: 'absolute'}}
            bodyStyle={{ padding: isMobile ? "12px" : "24px" }}
          >
            <AssignInvoices eventId={showRecord?.id} />
          </Drawer>
        }
      </Modal>
    </div>
  );
};